import { TaskPriority } from '@business/constants/task';
import { UserId, UserResponse } from '@business/entities/account';
import { OrgId } from '@business/entities/organization';
import { FormAjaxSelect, FormDropdownDate, FormInput } from '@core/components/form';
import { FormUserAvatar } from '@core/components/form/FormUserAvatar';
import { formControl, FormDataType, formGroup, useFormConfig } from '@core/utils/form';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { R } from '@core/utils/r';
import { toMoment } from '@core/utils/time';
import { GCDropdown } from '@modules/common/GCDropdown';
import { PrioritySelector } from '@modules/common/PrioritySelector';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Badge, Button } from 'reactstrap';
import { tap } from 'rxjs/operators';
import { TasksMenuBloc } from './TasksMenuBloc';
import { NewTaskBloc } from '@modules/notes/TabNotes/NewTask/NewTaskBloc';
import { TaskId } from '@business/entities/task';
import { TaskDetailModalBloc } from '@modules/tasks/TaskDetailModal/TaskDetailModalBloc';
import { TaskStatusResponse } from '@business/entities/task_status';
import { TaskStatusSelector } from '@modules/common/TaskStatusSelector';
import { UserService } from '@business/services';
import { Session } from '@modules/auth/session';

function defineTaskForm() {
  return formGroup({
    title: formControl<string>({
      placeholder: 'Enter task...',
      required: true,
    }),
    dueDate: formControl<number>({
      placeholder: 'Due Date',
    }),
    orgId: formControl<OrgId>({
      placeholder: `${gcLabel('Organization')}`,
    }),
    priority: formControl<TaskPriority>({
      initialValue: TaskPriority.none,
    }),
    status: formControl<TaskStatusResponse | undefined>({}),
    assignee: formControl<UserId[]>({
      initialValue: [],
    }),
  });
}

export type NewTaskFormData = FormDataType<typeof defineTaskForm>;
export type NewTaskForm = ReturnType<typeof defineTaskForm>;

export interface AddTaskProps {
  onReload?(): void;
  onSubmit?(): void;
  users: UserResponse[];
  bloc: TasksMenuBloc | NewTaskBloc | TaskDetailModalBloc;
  assignee?: UserId[];
  showTaskForm?: boolean;
  user: UserResponse | null;
  orgId?: OrgId;
  parentId?: TaskId;
  parentSectionId: string;
  statuses?: TaskStatusResponse[];
  onDone?(): void;
}

export const AddTask = (props: AddTaskProps) => {
  const { bloc, assignee, showTaskForm = false, user, orgId, parentId, parentSectionId, statuses = [] } = props;
  const form = useMemo(() => defineTaskForm(), []);
  const formValue = useNonNilObservable(form.value$);
  const [showForm, setShowForm] = useState(showTaskForm);

  const onSubmit = useCallback(
    (data: NewTaskFormData) => {
      //   if (assignee) {
      //     data.assignee = [...data.assignee, ...assignee];
      //   }
      if (orgId) {
        data = { ...data, orgId };
      }

      return bloc.createTask({ ...data, statusId: data.status ? data.status.id : undefined }, parentId).pipe(
        tap(() => {
          setShowForm(false);
          form.reset(undefined, { emit: false });
          props.onSubmit && props.onSubmit();
          props.onDone && props.onDone();
        }),
      );
    },
    [bloc, form, orgId, parentId, props],
  );

  useEffect(() => {
    if (assignee && assignee.length) {
      const assigneeId = R.toNumber(R.replace(R.first(assignee) || '', 'user-', '')) as any;
      form.patchValue({ assignee: [assigneeId] });
    }
  }, [assignee, form]);

  useFormConfig(form, { onSubmit });

  if (!showForm) {
    return (
      <div className="p-2">
        <a
          className="ml-4 text-muted font-size-10"
          onClick={() => {
            setShowForm(true);
            form.reset(undefined, { emit: false });
            if (assignee && assignee.length) {
              const assigneeId = R.toNumber(R.replace(R.first(assignee) || '', 'user-', '')) as any;
              form.patchValue({ assignee: [assigneeId] });
            }
          }}
        >
          <i className="fa fa-plus mr-1"></i> New task
        </a>
      </div>
    );
  }

  return (
    <>
      <div className="task-item w-100 task-tr-div" style={{ height: 60 }}>
        <div className="task-td-div task-w-move"></div>

        <div className="task-td-div task-w-name d-flex flex-column" style={{ alignItems: 'flex-start' }}>
          <FormInput
            autoFocus
            onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
              if (event.keyCode === 13) {
                form.submit();
              }
            }}
            withoutGroup
            control={form.controls.title}
          />
        </div>
        <div className="task-td-div task-w-form-assignee" style={{ flex: 1 }}>
          {(!!statuses.length || UserService.isAdmin(Session.user)) && (
            <TaskStatusSelector statuses={statuses} onSelect={status => form.patchValue({ status })} item={formValue.status} />
          )}
          <FormUserAvatar tooltipContainer={parentSectionId} control={form.controls.assignee} users={props.users} meId={user?.id} />
          <PrioritySelector priority={formValue.priority} onSelect={priority => form.patchValue({ priority })} withButton />
          <FormDropdownDate clearable control={form.controls.dueDate}>
            <Button outline color="dark" style={{ marginLeft: 10, position: 'relative', borderRadius: '50%', width: 35, height: 35 }}>
              <i className="far fa-calendar-check"></i>
              {!!formValue.dueDate && (
                <Badge className="pill translate-middle ml-2" style={{ fontSize: 12, position: 'absolute', top: -10, right: -20 }} color="dark">
                  {toMoment(formValue.dueDate).format('MM/DD')}
                </Badge>
              )}
            </Button>
          </FormDropdownDate>

          {!orgId && (
            <GCDropdown
              target={
                <Button
                  outline={R.toNumber(formValue.orgId) === 0}
                  color="dark"
                  style={{ marginLeft: 10, marginRight: 10, position: 'relative', borderRadius: '50%', width: 35, height: 35 }}
                >
                  <i className="far fa-building"></i>
                </Button>
              }
            >
              {setOpen => (
                <div style={{ width: 400, display: 'flex', flexDirection: 'column' }} className="p-2">
                  <FormAjaxSelect
                    autofocus
                    withoutGroup
                    noBody
                    control={form.controls.orgId}
                    callback={search => bloc.searchOrganizations(search)}
                    onChange={() => setOpen(false)}
                  />
                  {R.toNumber(formValue.orgId) > 0 && (
                    <a href="#" className="pt-2" onClick={() => form.patchValue({ orgId: undefined })}>
                      Clear
                    </a>
                  )}
                </div>
              )}
            </GCDropdown>
          )}
        </div>
        <div className="task-td-div task-w-save">
          <Button size="sm" outline onClick={() => form.submit()}>
            Save
          </Button>
          <a onClick={() => setShowForm(false)}>
            <i className="ml-2 fa fa-times p-2"></i>
          </a>
        </div>
      </div>
    </>
  );
};
